import type { FC, PropsWithChildren } from "react";

import { UIView, useCurrentStateAndParams } from "@uirouter/react";

import { Flex } from "@similarweb/ui-components/foundations";

import { DefaultErrorBoundary } from "app/components/error-boundary-no-data/DefaultErrorBoundry";
import { DemoBanner } from "app/pages/digital-marketing/monitor-and-protect/shared/components/demo-banner/demo-banner";

import { useMonitorAndProtectCurrentCampaign } from "../../services/api/campaign";
import { EmptyCampaignState, ErrorCampaignState, PageLoadingState } from "../../shared/components/page-states";
import { PreloadedHookProvider } from "../../shared/hooks/preloaded-hook";
import { MonitorAndProtectSubNav } from "../../shared/sub-navigation";
import { Content } from "../../shared/ui/layout/content";
import { NavContainer } from "../../shared/ui/layout/navigation-container";

import { ROUTES } from "./constants";

type MonitorAndProtectRootProps = {
    homeStateNames: string[];
};

type ContentSwitcherProps = PropsWithChildren<MonitorAndProtectRootProps>;

const MonitorAndProtectContentSwitcher: FC<ContentSwitcherProps> = ({ homeStateNames, children }) => {
    const { data, isLoading: loadingCampaign, isError } = useMonitorAndProtectCurrentCampaign();
    const { state } = useCurrentStateAndParams();

    // We don't block home page at all
    if (state.name && homeStateNames.includes(state.name)) {
        return children;
    }

    if (loadingCampaign) {
        return <PageLoadingState />;
    }

    if (isError) {
        return <ErrorCampaignState />;
    }

    if (!data) {
        return <EmptyCampaignState flex="1" />;
    }

    return children;
};

export const MonitorAndProtectRoot = ({ homeStateNames = [ROUTES.HOME.stateName] }: Partial<MonitorAndProtectRootProps>) => {
    return (
        <PreloadedHookProvider>
            <Flex
                height="100vh"
                flexDirection="column"
            >
                <NavContainer>
                    <MonitorAndProtectSubNav />
                </NavContainer>
                <Content>
                    <MonitorAndProtectContentSwitcher homeStateNames={homeStateNames}>
                        <DemoBanner />
                        <DefaultErrorBoundary>
                            <UIView className="fadeIn" />
                        </DefaultErrorBoundary>
                    </MonitorAndProtectContentSwitcher>
                </Content>
            </Flex>
        </PreloadedHookProvider>
    );
};
